import { Trans } from '@lingui/react/macro';

import { useCanaryOverviewProductAreaScores } from '@/api/aiVisibility';
import { useCurrentInstance } from '@/api/organization';
import { Empty } from '@/components/Empty';
import WidgetError from '@/components/errors/WidgetError';
import { Panel } from '@/components/panels';
import { Skeleton } from '@/components/Skeleton';
import { numberFormat } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';

const AIVisibilityOverviewProductAreaChart = () => {
  const { scores, isLoading, error } = useCanaryOverviewProductAreaScores();
  const { instance } = useCurrentInstance();

  return (
    <Panel
      title={<Trans>Competitive product areas</Trans>}
      subtitle={<Trans>Competitors' average vs your average visibility score.</Trans>}
      className="min-h-96"
    >
      {isLoading ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-9 w-full" />
          ))}
        </div>
      ) : error ? (
        <WidgetError message={<Trans>Unable to load product area scores.</Trans>} />
      ) : !scores?.length ? (
        <Empty>
          <Trans>No product area visibility scores available.</Trans>
        </Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-100 text-left text-sm">
            <thead className="text-xs text-gray-600">
              <tr>
                <th scope="col" className="sr-only">
                  <Trans>Product area</Trans>
                </th>
                <th scope="col" className="px-2 pb-2 text-right font-medium">
                  <Trans>Competitors</Trans>
                </th>
                <th scope="col" className="px-2 pb-2 text-right font-medium">
                  {instance?.name ?? <Trans>Your company</Trans>}
                </th>
                <th scope="col" className="sr-only">
                  <Trans>Change</Trans>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {scores.map((score) => {
                const customerLeads =
                  score.customerAverageScore != null &&
                  score.competitorAverageScore != null &&
                  score.customerAverageScore >= score.competitorAverageScore;

                return (
                  <tr key={score.productArea}>
                    <th scope="row" className="py-3 pr-2 font-semibold text-gray-900">
                      {score.productArea}
                    </th>
                    <td className="px-2 py-3 text-right text-gray-700">
                      {score.competitorAverageScore == null
                        ? '—'
                        : numberFormat(score.competitorAverageScore, { precision: 0 })}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-3 text-right font-semibold',
                        score.customerAverageScore == null || score.competitorAverageScore == null
                          ? 'text-gray-600'
                          : customerLeads
                            ? 'text-green-700'
                            : 'text-red-600',
                      )}
                    >
                      {score.customerAverageScore == null
                        ? '—'
                        : numberFormat(score.customerAverageScore, { precision: 0 })}
                    </td>
                    <td className="py-3 pl-2 text-right font-semibold whitespace-nowrap">
                      {score.customerChange == null ? (
                        '—'
                      ) : (
                        <span
                          className={cn(
                            score.customerChange > 0 && 'text-green-700',
                            score.customerChange < 0 && 'text-red-600',
                            score.customerChange === 0 && 'text-gray-600',
                          )}
                        >
                          {score.customerChange > 0 && '▲'}
                          {score.customerChange < 0 && '▼'}
                          {numberFormat(Math.abs(score.customerChange), {
                            isPercent: true,
                            precision: 0,
                          })}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

export default AIVisibilityOverviewProductAreaChart;
